<script setup lang="ts">
import type { RangePreset, RangePresetSet } from "./dateTime";
import { onClickOutside, useMediaQuery } from "@vueuse/core";
import { computed, nextTick, ref, useId, useSlots, watch } from "vue";
import { useI18n } from "vue-i18n";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VButton from "../Button/VButton.vue";
import VFieldLabel from "../Form/VFieldLabel.vue";
import VIcon from "../Icon/VIcon.vue";
import { useFieldId } from "../useFieldId";
import {
  addDays,
  dayKey,
  formatFieldDate,
  formatShortDate,
  isSameDay,
  rangePresets,
  startOfDay,
  timeOfDate,
} from "./dateTime";
import {
  addTimeChipCva,
  calendarDayCva,
  calendarDayInnerCva,
  navigationButtonCva,
  rangePresetCva,
  timeChipCva,
} from "./VDatePicker.cva";
import VPickerPopover from "./VPickerPopover.vue";
import VTimePanel from "./VTimePanel.vue";
import { fieldClearCva, fieldFrameCva } from "./VTimePicker.cva";

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  isSelected: boolean
  isDisabled: boolean
  isFocused: boolean
  band: "none" | "full" | "start" | "end" | "previewFull" | "previewStart" | "previewEnd"
  isPreviewEnd: boolean
}

type ModelValue = string | Date | Date[] | null;

interface Props {
  /** A date, or `[start, end]` with `range`. With a time the date carries it; without one it is at 00:00. */
  modelValue?: ModelValue
  placeholder?: string
  size?: "small" | "medium" | "large"
  labelLeft?: string
  required?: boolean
  disabled?: boolean
  errorMessage?: string | null
  /** Shows the field in its error state without a message, for a field whose message is rendered elsewhere. */
  invalid?: boolean
  hasPerms?: boolean
  /** Id of the field, for an external `<label for>`. Defaults to the surrounding field's id or a unique id. */
  id?: string
  /** Id of an element that names the field, for a label the caller renders elsewhere instead of `labelLeft`. */
  ariaLabelledby?: string
  /** Ids of elements that describe the field. */
  ariaDescribedby?: string
  /** A period: two clicks, start and end. */
  range?: boolean
  /** Period: the end can lie at most this many days after the chosen start. */
  maxRangeDays?: number
  /** Period: quick choices left of the calendar, looking back (filters) or forward (planning), or an own list. */
  presets?: RangePresetSet | RangePreset[]
  minDate?: Date | string
  maxDate?: Date | string
  locale?: string
  /** For recurring dates where only the day and month matter. */
  yearless?: boolean
  /**
   * A time after the date: `off` (only a date), `optional` (+ Tijd in the field) or `required` (the field asks for
   * a time until one is set). Not with `range`.
   */
  timeMode?: "off" | "optional" | "required"
  /** Whether the date carries a time (`v-model:timeSelected`). Without it a time other than 00:00 counts as one. */
  timeSelected?: boolean
  /** Step of the minute arrows and of Nu in the time picker. */
  minuteStep?: 1 | 5 | 10 | 15 | 30
  /** Quick times of the time picker, after Nu. */
  quickTimes?: string[]
  hideClear?: boolean
  showErrorBelow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: undefined,
  size: "medium",
  labelLeft: "",
  required: false,
  disabled: false,
  errorMessage: null,
  invalid: false,
  hasPerms: true,
  id: undefined,
  ariaLabelledby: undefined,
  ariaDescribedby: undefined,
  range: false,
  maxRangeDays: undefined,
  presets: undefined,
  minDate: undefined,
  maxDate: undefined,
  locale: undefined,
  yearless: false,
  timeMode: "off",
  timeSelected: undefined,
  minuteStep: 5,
  quickTimes: undefined,
  hideClear: false,
  showErrorBelow: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: Date | Date[] | null): void
  (e: "update:timeSelected", value: boolean): void
  (e: "blur", event: FocusEvent): void
  (e: "show"): void
  (e: "hide"): void
}>();

const slots = useSlots();
const { t, locale: i18nLocale } = useI18n();
const activeLocale = computed(() => props.locale ?? i18nLocale.value);
const id = useFieldId(() => props.id);
const errorId = useId();
const isDisabled = computed(() => props.disabled || !props.hasPerms);
const timeEnabled = computed(() => !props.range && props.timeMode !== "off");
const isSheet = useMediaQuery("(max-width: 639px)");

const wrapperRef = ref<HTMLElement | null>(null);
const anchorRef = ref<HTMLElement | null>(null);
const fieldButtonRef = ref<HTMLButtonElement | null>(null);
const datePopover = ref<InstanceType<typeof VPickerPopover> | null>(null);
const timePopover = ref<InstanceType<typeof VPickerPopover> | null>(null);
const timePanel = ref<InstanceType<typeof VTimePanel> | null>(null);
const openPanel = ref<"date" | "time" | null>(null);

watch(openPanel, (next, previous) => {
  if (next && !previous) {
    emit("show");
  } else if (!next && previous) {
    emit("hide");
  }
});

// --- Value ---

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}

function toBound(value: Date | string | undefined): Date | null {
  return value === undefined ? null : toDate(value);
}

const selectedDate = ref<Date | null>(null);
const selectedTime = ref<string | null>(null);
const rangeStart = ref<Date | null>(null);
const rangeEnd = ref<Date | null>(null);
const isSelectingRange = ref(false);
const hoverDate = ref<Date | null>(null);
const viewDate = ref(startOfDay(new Date()));
const focusedDate = ref<Date | null>(null);
const isKeyboardNavigation = ref(false);
const timeTouched = ref(false);

// What this picker emitted last, so a time of 00:00 the user chose is not read back as "no time".
let lastEmitted: { time: number, hasTime: boolean } | null = null;

function applySingle(value: ModelValue): void {
  const date = toDate(Array.isArray(value) ? (value[0] ?? null) : value);
  if (!date) {
    selectedDate.value = null;
    selectedTime.value = null;
    return;
  }
  selectedDate.value = startOfDay(date);
  if (!timeEnabled.value) {
    selectedTime.value = null;
    return;
  }
  const keepOwn = lastEmitted !== null && lastEmitted.time === date.getTime() && props.timeSelected === undefined;
  const hasTime = keepOwn
    ? (lastEmitted as { hasTime: boolean }).hasTime
    : (props.timeSelected ?? (date.getHours() !== 0 || date.getMinutes() !== 0));
  selectedTime.value = hasTime ? timeOfDate(date) : null;
}

function applyRange(value: ModelValue): void {
  const dates = (Array.isArray(value) ? value : [])
    .map(date => toDate(date))
    .filter((date): date is Date => date !== null)
    .map(startOfDay);
  hoverDate.value = null;
  rangeStart.value = dates[0] ?? null;
  rangeEnd.value = dates[1] ?? null;
  isSelectingRange.value = dates.length === 1;
}

function applyValue(value: ModelValue): void {
  if (props.range) {
    applyRange(value);
    return;
  }
  applySingle(value);
}

applyValue(props.modelValue);
viewDate.value = startOfMonth(selectedDate.value ?? rangeStart.value ?? new Date());

watch(() => props.modelValue, value => applyValue(value));
watch(() => props.timeSelected, () => applySingle(props.modelValue));

function emitSingle(date: Date | null, time: string | null): void {
  if (!date) {
    lastEmitted = null;
    emit("update:modelValue", null);
    if (timeEnabled.value) {
      emit("update:timeSelected", false);
    }
    return;
  }
  const value = new Date(date);
  if (time) {
    const [hours, minutes] = time.split(":").map(Number);
    value.setHours(hours, minutes, 0, 0);
  } else {
    value.setHours(0, 0, 0, 0);
  }
  lastEmitted = { time: value.getTime(), hasTime: Boolean(time) };
  emit("update:modelValue", value);
  if (timeEnabled.value) {
    emit("update:timeSelected", Boolean(time));
  }
}

const hasSelection = computed(() => (props.range ? rangeStart.value !== null : selectedDate.value !== null));
const hasTime = computed(() => timeEnabled.value && selectedTime.value !== null);

// --- Text in the field ---

function rangeText(from: Date, to: Date | null): string {
  const withYear = !props.yearless;
  if (!to || isSameDay(from, to)) {
    return formatShortDate(from, activeLocale.value, withYear);
  }
  return t("date_picker.range_text", {
    from: formatShortDate(from, activeLocale.value, withYear && from.getFullYear() !== to.getFullYear()),
    to: formatShortDate(to, activeLocale.value, withYear),
  });
}

const inputValue = computed(() => {
  if (props.range) {
    return rangeStart.value ? rangeText(rangeStart.value, rangeEnd.value) : "";
  }
  return selectedDate.value ? formatFieldDate(selectedDate.value, activeLocale.value, props.yearless) : "";
});

const inputPlaceholder = computed(() => {
  if (!props.showErrorBelow && props.errorMessage) {
    return props.errorMessage;
  }
  return props.placeholder ?? (props.range ? t("date_picker.period_placeholder") : t("date_picker.date_placeholder"));
});

// --- Calendar ---

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

const minDay = computed(() => toBound(props.minDate));
const maxDay = computed(() => toBound(props.maxDate));

function isDateDisabled(date: Date): boolean {
  const key = dayKey(date);
  if (minDay.value && key < dayKey(minDay.value)) {
    return true;
  }
  if (maxDay.value && key > dayKey(maxDay.value)) {
    return true;
  }
  if (props.range && isSelectingRange.value && rangeStart.value) {
    if (key < dayKey(rangeStart.value)) {
      return true;
    }
    if (props.maxRangeDays !== undefined && key > dayKey(addDays(rangeStart.value, props.maxRangeDays))) {
      return true;
    }
  }
  return false;
}

const previewDate = computed(() => {
  if (!props.range || !isSelectingRange.value || !rangeStart.value) {
    return null;
  }
  const candidate = hoverDate.value ?? (isKeyboardNavigation.value ? focusedDate.value : null);
  return candidate && !isDateDisabled(candidate) ? candidate : null;
});

function bandOf(date: Date): CalendarDay["band"] {
  if (!props.range || !rangeStart.value) {
    return "none";
  }
  const preview = previewDate.value !== null && !rangeEnd.value;
  const end = rangeEnd.value ?? previewDate.value;
  if (!end) {
    return "none";
  }
  const [low, high] = dayKey(end) < dayKey(rangeStart.value) ? [end, rangeStart.value] : [rangeStart.value, end];
  const key = dayKey(date);
  if (key < dayKey(low) || key > dayKey(high) || dayKey(low) === dayKey(high)) {
    return "none";
  }
  if (key === dayKey(low)) {
    return preview ? "previewStart" : "start";
  }
  if (key === dayKey(high)) {
    return preview ? "previewEnd" : "end";
  }
  return preview ? "previewFull" : "full";
}

const weeks = computed<CalendarDay[][]>(() => {
  const first = startOfMonth(viewDate.value);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const rows = Math.ceil((offset + daysInMonth) / 7);
  const start = addDays(first, -offset);
  const today = new Date();
  return Array.from({ length: rows }, (_, row) => Array.from({ length: 7 }, (_, column) => {
    const date = addDays(start, row * 7 + column);
    const isPreviewEnd = previewDate.value !== null
      && !rangeEnd.value
      && isSameDay(date, previewDate.value)
      && !isSameDay(date, rangeStart.value);
    const isSelected = props.range
      ? isSameDay(date, rangeStart.value) || isSameDay(date, rangeEnd.value)
      : isSameDay(date, selectedDate.value);
    return {
      date,
      isCurrentMonth: date.getMonth() === first.getMonth(),
      isToday: isSameDay(date, today),
      isSelected,
      isDisabled: isDateDisabled(date),
      isFocused: isKeyboardNavigation.value && isSameDay(date, focusedDate.value),
      band: bandOf(date),
      isPreviewEnd,
    };
  }));
});

const rowHeight = computed(() => (isSheet.value ? 46 : 36));
const calendarViewportHeight = computed(() => weeks.value.length * rowHeight.value);

const monthTitle = computed(() => new Intl.DateTimeFormat(activeLocale.value, {
  month: "long",
  year: props.yearless ? undefined : "numeric",
}).format(viewDate.value));

// Monday first, short weekday names of the locale (2024-01-01 is a Monday).
const weekDays = computed(() => {
  const formatter = new Intl.DateTimeFormat(activeLocale.value, { weekday: "short" });
  return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(2024, 0, 1 + index)));
});

const dayLabelFormatter = computed(() => new Intl.DateTimeFormat(activeLocale.value, {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: props.yearless ? undefined : "numeric",
}));

function dayLabel(day: CalendarDay): string {
  const label = dayLabelFormatter.value.format(day.date);
  return day.isDisabled ? t("date_picker.day_unavailable", { date: label }) : label;
}

function dayInnerState(day: CalendarDay): "idle" | "today" | "selected" | "previewEnd" {
  if (day.isPreviewEnd) {
    return "previewEnd";
  }
  if (day.isSelected) {
    return "selected";
  }
  return day.isToday ? "today" : "idle";
}

function dayClasses(day: CalendarDay, sheet: boolean): string {
  return calendarDayCva({ sheet, isCurrentMonth: day.isCurrentMonth, isDisabled: day.isDisabled, band: day.band });
}

function dayInnerClasses(day: CalendarDay, sheet: boolean): string {
  return calendarDayInnerCva({
    sheet,
    state: dayInnerState(day),
    isDisabled: day.isDisabled,
    isFocused: day.isFocused,
  });
}

// Month changes slide the grid sideways.
const slideDirection = ref<"left" | "right" | null>(null);

function moveMonth(delta: number): void {
  slideDirection.value = delta > 0 ? "left" : "right";
  viewDate.value = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() + delta, 1);
}

function moveYear(delta: number): void {
  moveMonth(delta * 12);
}

// --- Picking ---

function pickDay(day: Pick<CalendarDay, "date" | "isDisabled">): void {
  if (day.isDisabled) {
    return;
  }
  if (props.range) {
    pickRangeDay(day.date);
    return;
  }
  selectedDate.value = startOfDay(day.date);
  emitSingle(selectedDate.value, hasTime.value ? selectedTime.value : null);
  closeAll();
  focusField();
}

function pickRangeDay(date: Date): void {
  if (!rangeStart.value || rangeEnd.value) {
    rangeStart.value = startOfDay(date);
    rangeEnd.value = null;
    isSelectingRange.value = true;
    hoverDate.value = null;
    // The keyboard preview of the end starts at the start itself.
    focusedDate.value = startOfDay(date);
    return;
  }
  const start = rangeStart.value;
  const [from, to] = dayKey(date) < dayKey(start) ? [startOfDay(date), start] : [start, startOfDay(date)];
  rangeStart.value = from;
  rangeEnd.value = to;
  isSelectingRange.value = false;
  hoverDate.value = null;
  emit("update:modelValue", [from, to]);
  closeAll();
  focusField();
}

function setToday(): void {
  pickDay({ date: startOfDay(new Date()), isDisabled: isDateDisabled(new Date()) });
}

function clearValue(): void {
  timeTouched.value = false;
  if (props.range) {
    rangeStart.value = null;
    rangeEnd.value = null;
    isSelectingRange.value = false;
    hoverDate.value = null;
    emit("update:modelValue", []);
  } else {
    selectedDate.value = null;
    selectedTime.value = null;
    emitSingle(null, null);
  }
  closeAll();
}

// --- Presets ---

const presetList = computed(() => {
  if (!props.range || !props.presets) {
    return [];
  }
  const list = Array.isArray(props.presets)
    ? props.presets.map(preset => ({ ...preset, text: preset.label }))
    : rangePresets(props.presets).map(preset => ({ ...preset, text: t(`date_picker.presets.${preset.label}`) }));
  return list.map((preset) => {
    const from = startOfDay(preset.from);
    const to = startOfDay(preset.to);
    const tooLong = props.maxRangeDays !== undefined && dayKey(to) > dayKey(addDays(from, props.maxRangeDays));
    const outside = (minDay.value !== null && dayKey(from) < dayKey(minDay.value))
      || (maxDay.value !== null && dayKey(to) > dayKey(maxDay.value));
    return {
      key: `${preset.text}-${dayKey(from)}`,
      label: preset.text,
      from,
      to,
      hint: rangeText(from, to),
      active: !isSelectingRange.value && isSameDay(from, rangeStart.value) && isSameDay(to, rangeEnd.value),
      disabled: tooLong || outside,
    };
  });
});

const activePresetLabel = computed(() => presetList.value.find(preset => preset.active)?.label ?? null);

function pickPreset(preset: { from: Date, to: Date, disabled: boolean }): void {
  if (preset.disabled) {
    return;
  }
  rangeStart.value = preset.from;
  rangeEnd.value = preset.to;
  isSelectingRange.value = false;
  hoverDate.value = null;
  viewDate.value = startOfMonth(preset.from);
  emit("update:modelValue", [preset.from, preset.to]);
  closeAll();
  focusField();
}

const rangeSummary = computed(() => {
  if (!rangeStart.value) {
    return presetList.value.length > 0 ? t("date_picker.range_pick_start_or_preset") : t("date_picker.range_pick_start");
  }
  if (!rangeEnd.value) {
    return t("date_picker.range_pick_end");
  }
  const days = Math.round((rangeEnd.value.getTime() - rangeStart.value.getTime()) / 86_400_000) + 1;
  return `${rangeText(rangeStart.value, rangeEnd.value)} (${t("date_picker.days_count", days)})`;
});

// --- Time ---

/** The bounds of the time picker on the first and the last day that can be picked. */
const timeBounds = computed(() => {
  const date = selectedDate.value;
  const min = minDay.value && date && isSameDay(date, minDay.value) && timeOfDate(minDay.value) !== "00:00" ? timeOfDate(minDay.value) : null;
  const max = maxDay.value && date && isSameDay(date, maxDay.value) && timeOfDate(maxDay.value) !== "00:00" ? timeOfDate(maxDay.value) : null;
  return { min, max };
});

function commitTime(time: string): void {
  selectedTime.value = time;
  timeTouched.value = false;
  emitSingle(selectedDate.value, time);
  closeAll();
  focusField();
}

function removeTime(): void {
  selectedTime.value = null;
  timeTouched.value = true;
  emitSingle(selectedDate.value, null);
  closeAll();
  focusField();
}

function onTimePanelClear(): void {
  if (selectedTime.value) {
    removeTime();
    return;
  }
  closeAll();
  focusField();
}

const timeMissing = computed(() => props.timeMode === "required" && selectedDate.value !== null && !hasTime.value);
const showTimeError = computed(() => timeMissing.value && timeTouched.value);
const timeClearLabel = computed(() => (selectedTime.value ? t("date_picker.no_time") : t("base.cancel")));

// --- Opening and closing ---

function focusField(): void {
  void nextTick(() => fieldButtonRef.value?.focus({ preventScroll: true }));
}

function initializeFocusedDate(): void {
  focusedDate.value = selectedDate.value ?? rangeStart.value ?? startOfDay(new Date());
  isKeyboardNavigation.value = false;
}

function show(): void {
  if (isDisabled.value) {
    return;
  }
  if (openPanel.value === "time") {
    timePopover.value?.hide();
  }
  viewDate.value = startOfMonth(selectedDate.value ?? rangeStart.value ?? new Date());
  slideDirection.value = null;
  initializeFocusedDate();
  datePopover.value?.show();
}

function showTime(): void {
  if (isDisabled.value || !timeEnabled.value || !selectedDate.value) {
    return;
  }
  if (openPanel.value === "date") {
    datePopover.value?.hide();
  }
  timePopover.value?.show();
}

function toggleDate(): void {
  if (openPanel.value === "date") {
    datePopover.value?.hide();
    return;
  }
  show();
}

function closeAll(): void {
  datePopover.value?.hide();
  timePopover.value?.hide();
}

function onDateShow(): void {
  openPanel.value = "date";
}

function onDateHide(): void {
  if (openPanel.value === "date") {
    openPanel.value = null;
  }
  // A period left with only its start was never emitted: the field goes back to the period it holds.
  if (props.range && isSelectingRange.value) {
    applyRange(props.modelValue);
  }
  hoverDate.value = null;
  focusedDate.value = null;
  isKeyboardNavigation.value = false;
}

function onTimeShow(): void {
  openPanel.value = "time";
  if (!isSheet.value) {
    void nextTick(() => timePanel.value?.focusHours());
  }
}

function onTimeHide(): void {
  if (openPanel.value === "time") {
    openPanel.value = null;
  }
  if (timeMissing.value) {
    timeTouched.value = true;
  }
  const active = document.activeElement;
  if (!active || active === document.body) {
    focusField();
  }
}

onClickOutside(wrapperRef, () => {
  if (openPanel.value) {
    closeAll();
  }
}, { ignore: [".v-popper__popper", ".v-picker-sheet"] });

function onFieldBlur(event: FocusEvent): void {
  emit("blur", event);
  const next = event.relatedTarget as Node | null;
  if (!openPanel.value && timeMissing.value && !(next && wrapperRef.value?.contains(next))) {
    timeTouched.value = true;
  }
}

// --- Keyboard ---

const OPEN_KEYS = new Set(["Enter", " ", "ArrowDown"]);

function moveFocusedDate(direction: "up" | "down" | "left" | "right" | "home" | "end"): void {
  const current = focusedDate.value ?? startOfDay(new Date());
  isKeyboardNavigation.value = true;
  let next: Date;
  switch (direction) {
    case "up":
      next = addDays(current, -7);
      break;
    case "down":
      next = addDays(current, 7);
      break;
    case "left":
      next = addDays(current, -1);
      break;
    case "right":
      next = addDays(current, 1);
      break;
    case "home":
      next = new Date(current.getFullYear(), current.getMonth(), 1);
      break;
    case "end":
      next = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      break;
  }
  focusedDate.value = next;
  if (next.getMonth() !== viewDate.value.getMonth() || next.getFullYear() !== viewDate.value.getFullYear()) {
    slideDirection.value = dayKey(next) > dayKey(viewDate.value) ? "left" : "right";
    viewDate.value = startOfMonth(next);
  }
}

const NAVIGATION_KEYS: Record<string, "up" | "down" | "left" | "right" | "home" | "end"> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  Home: "home",
  End: "end",
};

/** Keys while the calendar is open, from the field or from inside the calendar. */
function onCalendarKeydown(event: KeyboardEvent): void {
  if (event.defaultPrevented) {
    return;
  }
  const direction = NAVIGATION_KEYS[event.key];
  if (direction) {
    event.preventDefault();
    moveFocusedDate(direction);
    return;
  }
  if (event.key === "PageUp" || event.key === "PageDown") {
    event.preventDefault();
    const delta = event.key === "PageUp" ? -1 : 1;
    if (event.shiftKey) {
      moveYear(delta);
    } else {
      moveMonth(delta);
    }
    if (focusedDate.value) {
      // The same day of the month, or the last day of a shorter month.
      const lastDay = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() + 1, 0).getDate();
      const day = Math.min(focusedDate.value.getDate(), lastDay);
      focusedDate.value = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth(), day);
    }
    return;
  }
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    datePopover.value?.hide();
    focusField();
    return;
  }
  const onOtherButton = event.target instanceof HTMLElement && event.target !== fieldButtonRef.value && event.target.closest("button") !== null;
  if ((event.key === "Enter" || event.key === " ") && !onOtherButton) {
    event.preventDefault();
    if (focusedDate.value) {
      pickDay({ date: focusedDate.value, isDisabled: isDateDisabled(focusedDate.value) });
    }
  }
}

function onFieldKeydown(event: KeyboardEvent): void {
  if (isDisabled.value) {
    return;
  }
  if (openPanel.value === "date") {
    onCalendarKeydown(event);
    return;
  }
  if (OPEN_KEYS.has(event.key)) {
    event.preventDefault();
    show();
  }
}

// --- Field ---

const usesCustomInput = computed(() => Boolean(slots.input));
const shownError = computed(() => props.errorMessage ?? (showTimeError.value ? t("date_picker.time_required") : null));
const hasError = computed(() => props.invalid || Boolean(props.errorMessage) || showTimeError.value);

// The field is a combobox: its label names it (`<label for>` or `ariaLabelledby`) and its text is its value.
const fieldDescribedby = computed(() => {
  const parts = [props.ariaDescribedby, shownError.value && props.showErrorBelow ? errorId : undefined].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
});

const frameClasses = computed(() => fieldFrameCva({
  size: props.size,
  state: isDisabled.value ? "disabled" : hasError.value ? "error" : openPanel.value ? "open" : "idle",
}));
const clearClasses = computed(() => fieldClearCva());
const addTimeClasses = computed(() => addTimeChipCva({
  state: openPanel.value === "time" ? "active" : showTimeError.value || (timeMissing.value && Boolean(props.errorMessage)) ? "error" : "idle",
}));
const timeChipClasses = computed(() => timeChipCva({ active: openPanel.value === "time" }));
const navigationClasses = computed(() => navigationButtonCva({ sheet: isSheet.value }));
const footerButtonClass = computed(() => (isSheet.value ? "flex-1 h-11! text-[15px]!" : ""));
const showClear = computed(() => hasSelection.value && !props.hideClear && !isDisabled.value);
const dateDialogLabel = computed(() => (props.range ? t("date_picker.period_placeholder") : t("date_picker.date_placeholder")));
const timeDialogLabel = computed(() => t("date_picker.time_placeholder_required"));

function anchorNode(): Element {
  return anchorRef.value ?? (wrapperRef.value as Element);
}

/** Shows "Kies een tijd" when a required time is missing, e.g. after a save attempt. Returns whether the time is there. */
function validate(): boolean {
  if (timeMissing.value) {
    timeTouched.value = true;
    return false;
  }
  return true;
}

defineExpose({
  show,
  showTime,
  hide: closeAll,
  validate,
});
</script>

<template>
  <div ref="wrapperRef" class="relative" :class="usesCustomInput ? 'inline-block' : 'block w-full'">
    <VFieldLabel
      v-if="labelLeft"
      :label="labelLeft"
      :for="id"
      :required="required"
      display="inline"
    />
    <ACTooltip type="edit_field" :allow-change="hasPerms">
      <VPickerPopover
        ref="datePopover"
        :label="dateDialogLabel"
        :disabled="isDisabled"
        @show="onDateShow"
        @hide="onDateHide"
      >
        <div ref="anchorRef">
          <slot
            :id="id"
            name="input"
            :input-value="inputValue"
            :show="show"
            :show-time="showTime"
            :clear-value="clearValue"
            :placeholder="inputPlaceholder"
            :disabled="disabled"
            :is-disabled="isDisabled"
            :has-time="hasTime"
            :time-text="selectedTime"
            :preset-label="activePresetLabel"
            :is-open="openPanel !== null"
          >
            <div :class="frameClasses" data-test="date-field">
              <button
                :id="id"
                ref="fieldButtonRef"
                type="button"
                role="combobox"
                class="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-[25px] border-0 bg-transparent py-0 pr-2 pl-3.5 text-left text-[length:inherit] whitespace-nowrap outline-hidden disabled:cursor-not-allowed"
                :class="isDisabled ? 'text-gray-500' : 'text-gray-900 dark:text-gray-100'"
                :disabled="isDisabled"
                aria-haspopup="dialog"
                :aria-expanded="openPanel === 'date'"
                :aria-required="required || undefined"
                :aria-invalid="hasError ? 'true' : undefined"
                :aria-labelledby="ariaLabelledby"
                :aria-describedby="fieldDescribedby"
                data-test="date-field-button"
                @click="toggleDate"
                @keydown="onFieldKeydown"
                @blur="onFieldBlur"
              >
                <VIcon name="calendar-1" class="shrink-0 text-[15px] text-gray-600 dark:text-gray-400" aria-hidden="true" />
                <span v-if="inputValue" class="min-w-0 truncate tabular-nums" data-test="date-field-value">{{ inputValue }}</span>
                <span v-else class="min-w-0 truncate text-gray-400 dark:text-dark-400" data-test="date-field-placeholder">{{ inputPlaceholder }}</span>
              </button>
              <button
                v-if="timeEnabled && selectedDate && !hasTime && !isDisabled"
                type="button"
                :class="addTimeClasses"
                aria-haspopup="dialog"
                :aria-expanded="openPanel === 'time'"
                :title="t('base.add_time')"
                :aria-label="t('base.add_time')"
                data-test="add-time"
                @click="showTime"
              >
                <VIcon name="plus" class="text-[11px]" aria-hidden="true" />
                {{ t("date_picker.time") }}
              </button>
              <span v-if="hasTime" :class="timeChipClasses" data-test="time-chip">
                <button
                  type="button"
                  class="inline-flex h-full cursor-pointer items-center gap-1 border-0 bg-transparent py-0 pr-1 pl-2 text-xs leading-4 font-medium tabular-nums text-inherit hover:bg-primary-100 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed dark:hover:bg-primary-900/40"
                  :disabled="isDisabled"
                  aria-haspopup="dialog"
                  :aria-expanded="openPanel === 'time'"
                  :aria-label="t('date_picker.change_time', { time: selectedTime })"
                  data-test="time-chip-change"
                  @click="showTime"
                >
                  <VIcon name="clock" class="text-[12px]" aria-hidden="true" />
                  {{ selectedTime }}
                </button>
                <button
                  v-if="!isDisabled"
                  type="button"
                  class="inline-flex h-full aspect-square cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-inherit hover:bg-primary-100 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500 dark:hover:bg-primary-900/40"
                  :aria-label="t('date_picker.remove_time')"
                  :title="t('date_picker.remove_time')"
                  data-test="time-chip-remove"
                  @click="removeTime"
                >
                  <VIcon name="x" class="text-[11px]" aria-hidden="true" />
                </button>
              </span>
              <button
                v-if="showClear"
                type="button"
                :class="clearClasses"
                :aria-label="range ? t('date_picker.clear_period') : t('date_picker.clear_date')"
                data-test="date-field-clear"
                @click.stop="clearValue"
              >
                <VIcon name="x" class="text-sm" aria-hidden="true" />
              </button>
              <span v-else-if="!hasPerms" class="mr-3 inline-flex text-gray-500" aria-hidden="true" data-test="date-field-lock">
                <VIcon name="lock" class="text-[13px]" />
              </span>
            </div>
          </slot>
        </div>

        <template #panel="{ sheet }">
          <div class="flex flex-col gap-2.5" @keydown="onCalendarKeydown">
            <div class="flex gap-3" :class="{ 'flex-col': sheet }">
              <div
                v-if="presetList.length > 0"
                :class="sheet ? 'grid grid-cols-2 gap-1' : 'flex w-36 shrink-0 flex-col border-r border-gray-200 pr-2 dark:border-dark-600'"
                role="group"
                :aria-label="t('date_picker.quick_choices')"
              >
                <button
                  v-for="preset in presetList"
                  :key="preset.key"
                  v-tooltip="{ content: preset.hint, placement: 'left', theme: 'verbleif-tooltip-solid' }"
                  type="button"
                  :class="[rangePresetCva({ active: preset.active }), { 'h-11 text-[15px]': sheet }]"
                  :aria-pressed="preset.active"
                  :disabled="preset.disabled"
                  data-test="range-preset"
                  @click="pickPreset(preset)"
                >
                  {{ preset.label }}<span class="sr-only">, {{ preset.hint }}</span>
                </button>
              </div>

              <div class="flex flex-col gap-1.5" :class="sheet ? 'w-full' : 'w-[264px]'" @mouseleave="hoverDate = null">
                <div class="flex items-center justify-between gap-2 pb-0.5">
                  <button
                    type="button"
                    :class="navigationClasses"
                    :aria-label="t('date_picker.previous_month')"
                    data-test="calendar-nav-previous-month"
                    @click="moveMonth(-1)"
                  >
                    <VIcon name="chevron-left" aria-hidden="true" />
                  </button>
                  <span class="text-sm leading-5 font-medium text-gray-800 capitalize dark:text-gray-200" aria-live="polite" data-test="calendar-title">{{ monthTitle }}</span>
                  <button
                    type="button"
                    :class="navigationClasses"
                    :aria-label="t('date_picker.next_month')"
                    data-test="calendar-nav-next-month"
                    @click="moveMonth(1)"
                  >
                    <VIcon name="chevron-right" aria-hidden="true" />
                  </button>
                </div>

                <div class="grid grid-cols-7" aria-hidden="true">
                  <span
                    v-for="(weekDay, index) in weekDays"
                    :key="index"
                    class="pt-0.5 pb-1 text-center text-[11px] leading-4 font-medium text-gray-500"
                    data-test="weekday-header"
                  >
                    {{ weekDay }}
                  </span>
                </div>

                <div
                  class="relative overflow-hidden"
                  data-test="calendar-viewport"
                  :style="{ height: `${calendarViewportHeight}px` }"
                >
                  <Transition :name="`calendar-${slideDirection ?? 'none'}`" mode="out-in">
                    <div :key="`${viewDate.getFullYear()}-${viewDate.getMonth()}`" class="absolute inset-0">
                      <div v-for="(week, weekIndex) in weeks" :key="weekIndex" class="grid grid-cols-7">
                        <button
                          v-for="day in week"
                          :key="day.date.getTime()"
                          type="button"
                          :class="dayClasses(day, sheet)"
                          :disabled="day.isDisabled"
                          :aria-label="dayLabel(day)"
                          :aria-pressed="day.isSelected"
                          :aria-current="day.isToday ? 'date' : undefined"
                          :data-today="day.isToday || undefined"
                          :data-band="day.band"
                          data-test="calendar-day"
                          @click="pickDay(day)"
                          @mouseenter="range && isSelectingRange && !day.isDisabled ? hoverDate = day.date : undefined"
                        >
                          <span
                            :class="dayInnerClasses(day, sheet)"
                          >
                            {{ day.date.getDate() }}
                          </span>
                        </button>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>
            </div>

            <div v-if="range" class="flex items-center gap-1.5 border-t border-gray-200 pt-2.5 dark:border-dark-600">
              <span class="min-w-0 text-[13px] leading-[18px] text-gray-700 dark:text-gray-300" aria-live="polite" data-test="range-summary">{{ rangeSummary }}</span>
              <span class="grow" />
              <VButton
                v-if="!hideClear"
                data-test="footer-clear"
                size="small"
                variant="gray"
                :class="footerButtonClass"
                :disabled="!hasSelection"
                static
                @click="clearValue"
              >
                {{ t("date_picker.clear") }}
              </VButton>
            </div>
            <div v-else class="mt-1 flex items-center gap-1.5 border-t border-gray-200 pt-2.5 dark:border-dark-600">
              <VButton
                data-test="footer-today"
                size="small"
                variant="light"
                :class="footerButtonClass"
                :disabled="isDateDisabled(new Date())"
                static
                @click="setToday"
              >
                {{ t("base.today") }}
              </VButton>
              <VButton
                v-if="!hideClear"
                data-test="footer-clear"
                size="small"
                variant="gray"
                :class="footerButtonClass"
                :disabled="!hasSelection"
                static
                @click="clearValue"
              >
                {{ t("date_picker.clear") }}
              </VButton>
              <span v-if="!sheet" class="grow" />
              <VButton
                v-if="timeEnabled && selectedDate"
                data-test="footer-time"
                size="small"
                variant="gray"
                :icon-left="hasTime ? 'clock' : 'plus'"
                :class="footerButtonClass"
                static
                @click="showTime"
              >
                {{ hasTime ? selectedTime : t("date_picker.time") }}
              </VButton>
            </div>
          </div>
        </template>
      </VPickerPopover>

      <VPickerPopover
        v-if="timeEnabled"
        ref="timePopover"
        :label="timeDialogLabel"
        placement="bottom-end"
        :reference-node="anchorNode"
        :disabled="isDisabled"
        @show="onTimeShow"
        @hide="onTimeHide"
      >
        <template #panel="{ sheet }">
          <VTimePanel
            ref="timePanel"
            :value="selectedTime"
            :minute-step="minuteStep"
            :quick-times="quickTimes"
            :min-time="timeBounds.min"
            :max-time="timeBounds.max"
            :clear-label="timeClearLabel"
            :sheet="sheet"
            @commit="commitTime"
            @clear="onTimePanelClear"
            @cancel="closeAll(); focusField()"
          />
        </template>
      </VPickerPopover>
    </ACTooltip>
    <div v-if="shownError && showErrorBelow" :id="errorId" class="mt-1 text-xs leading-4 text-danger-800 dark:text-danger-400" data-test="date-field-error">
      {{ shownError }}
    </div>
  </div>
</template>

<style scoped>
.calendar-left-enter-active,
.calendar-right-enter-active,
.calendar-left-leave-active,
.calendar-right-leave-active {
  transition: transform 0.15s ease-out, opacity 0.15s ease-out;
}

.calendar-left-enter-from,
.calendar-right-leave-to {
  transform: translateX(30px);
  opacity: 0;
}

.calendar-left-leave-to,
.calendar-right-enter-from {
  transform: translateX(-30px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .calendar-left-enter-active,
  .calendar-right-enter-active,
  .calendar-left-leave-active,
  .calendar-right-leave-active {
    transition: none;
  }
}
</style>
